// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { makeHandlerChainAwaitableCustom } from "@guestsupply/App/Handlers/Helpers/CustomHandlerCreator";
import {
    getAutoCarriers,
    getAutoDelivery,
    getAutoDeliveryCollection as getAutoDeliveryCollectionService,
    GetAutoDeliveryModel,
    GetCarriersModel,
    getEstimateTotals,
    GetEstimateTotalsModel,
} from "@guestsupply/App/Services/AutoDelivery";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import {
    ApiHandler,
    createHandlerChainRunner,
    HasOnError,
    HasOnSuccess,
} from "@insite/client-framework/HandlerCreator";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import loadCurrentShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTo";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";

interface Parameter {
    productId: string;
    productContext?: ProductContextModel;
}

interface Props {
    product?: ProductModel;
    productInfo?: ProductInfo;
    estimatedTotals?: GetEstimateTotalsModel;
    autoDeliveryData?: GetAutoDeliveryModel;
    autoCarriersData?: GetCarriersModel;
    currentShipViaId?: string;
    error?: string;
}

type HandlerType = ApiHandler<Parameter & HasOnSuccess & HasOnError<string>, Props>;

export const PopulateApiParameter: HandlerType = props => {
    props.apiParameter = { ...props.parameter };
};

export const LoadProductInfoListContext: HandlerType = async props => {
    if (!props.parameter.productContext) {
        const loadProductInfoListAwaitable = makeHandlerChainAwaitableCustom(loadProductInfoList);
        try {
            const result: any = await loadProductInfoListAwaitable({
                id: "AutoDeliveryModal",
                getProductCollectionParameter: {
                    productIds: [props.apiParameter.productId],
                    expand: ["images", "badges", "attributes", "properties"],
                },
            })(props.dispatch, props.getState);
            props.apiResult = { product: result?.products[0], productInfo: result.productInfos[0] };
        } catch (e) {
            props.apiResult = {
                ...props.apiResult,
                error: e?.message || translate("Failed to Fetch Product Details"),
            };
        }
    }
};

export const GetAutoDeliveryFromApi: HandlerType = async props => {
    const autoDelivery = await getAutoDelivery({
        productId: props.apiParameter.productId,
    });
    if (autoDelivery.successful) {
        props.apiResult = { ...props.apiResult, autoDeliveryData: autoDelivery?.result };
    } else {
        props.apiResult = {
            ...props.apiResult,
            error: autoDelivery?.errorMessage || translate("An error has occurred"),
        };
    }
};

export const GetAutoCarriersFromApi: HandlerType = async props => {
    interface AutoCarriersModel {
        result?: GetCarriersModel;
        successful: boolean;
        errorMessage?: string;
    }
    const autoCarriers: AutoCarriersModel = await getAutoCarriers();
    if (autoCarriers.successful) {
        (autoCarriers?.result as GetCarriersModel)?.carrierDtos.forEach(item => {
            if (item.shipVias) {
                item.shipVias.forEach(shipVia => {
                    if (shipVia.isDefault) {
                        props.apiResult = { ...props.apiResult, currentShipViaId: shipVia.id };
                    }
                });
            }
            props.apiResult = { ...props.apiResult, autoCarriersData: autoCarriers?.result };
        });
    } else {
        props.apiResult = {
            ...props.apiResult,
            error: autoCarriers?.errorMessage || translate("An error has occurred"),
        };
    }
};

export const getEstimateTotalsFromApi: HandlerType = async props => {
    let estimatedTotalsParameters;
    if (props.parameter.productContext) {
        estimatedTotalsParameters = {
            productId: props.parameter.productContext.productInfo.productId,
            qty: props.apiResult.autoDeliveryData?.customerAutoDeliveryDto
                ? props.apiResult.autoDeliveryData.customerAutoDeliveryDto?.qty
                : props.parameter.productContext.productInfo?.qtyOrdered,
            shipViaId: props.apiResult.currentShipViaId || undefined,
            uom: props.apiResult.autoDeliveryData?.customerAutoDeliveryDto
                ? props.apiResult.autoDeliveryData.customerAutoDeliveryDto?.uom
                : props.parameter.productContext.productInfo.unitOfMeasure,
        };
    } else {
        estimatedTotalsParameters = {
            productId: props.apiResult?.productInfo?.productId,
            qty: props.apiResult.autoDeliveryData?.customerAutoDeliveryDto
                ? props.apiResult.autoDeliveryData.customerAutoDeliveryDto?.qty
                : props.apiResult?.productInfo?.qtyOrdered,
            shipViaId: props.apiResult.currentShipViaId || undefined,
            uom: props.apiResult.autoDeliveryData?.customerAutoDeliveryDto
                ? props.apiResult.autoDeliveryData.customerAutoDeliveryDto?.uom
                : props.apiResult?.productInfo?.unitOfMeasure,
        };
    }
    const estimatedTotals = await getEstimateTotals(estimatedTotalsParameters as any);

    if (estimatedTotals.successful) {
        props.apiResult = { ...props.apiResult, estimatedTotals: estimatedTotals?.result };
    } else {
        props.apiResult = {
            ...props.apiResult,
            error: estimatedTotals?.errorMessage || translate("An error has occurred"),
        };
    }
};

export const DispatchSetAutoDeliveryModalData: HandlerType = props => {
    let productContextNew;
    if (props.parameter?.productContext) {
        productContextNew = props.apiResult.autoDeliveryData?.customerAutoDeliveryDto
            ? {
                  ...props.parameter.productContext,
                  productInfo: {
                      ...props.parameter.productContext.productInfo,
                      qtyOrdered: props.apiResult.autoDeliveryData.customerAutoDeliveryDto?.qty,
                  },
              }
            : props.parameter.productContext;
    } else {
        const productInfo = getProductInfoFromList(props.getState(), "AutoDeliveryModal", props.parameter.productId);
        const productInfoNew = props.apiResult.autoDeliveryData
            ? { ...productInfo, qtyOrdered: props.apiResult.autoDeliveryData.customerAutoDeliveryDto?.qty }
            : productInfo;
        productContextNew = { product: props.apiResult.product, productInfo: productInfoNew };
    }

    props.dispatch({
        type: "xAutoDelivery/CompleteSetModalData",
        estimateTotalsData: props.apiResult.estimatedTotals,
        autoDeliveryAndCarriersData: {
            autoDeliveryData: props.apiResult.autoDeliveryData,
            autoCarriersData: props.apiResult.autoCarriersData,
        },
        productContext: productContextNew,
    } as any);
};
export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (!props.apiResult.error) {
        props.parameter.onSuccess?.();
    }
};
export const ExecuteOnErrorCallback: HandlerType = props => {
    if (props.apiResult?.error) {
        props.parameter.onError?.(props.apiResult?.error as any);
    }
};

export const chain = [
    PopulateApiParameter,
    LoadProductInfoListContext,
    GetAutoDeliveryFromApi,
    GetAutoCarriersFromApi,
    getEstimateTotalsFromApi,
    DispatchSetAutoDeliveryModalData,
    ExecuteOnSuccessCallback,
    ExecuteOnErrorCallback,
];

const handleAutoDeliveryModalData = createHandlerChainRunner(chain, "HandleAutoDeliveryModalData");
export default handleAutoDeliveryModalData;
